import type {
  LastPaymentPublic,
  PolicyLoanSummaryPublic,
  PolicyStatus,
  PolicyThisMonth,
} from './policy';

/** Money handed over in person or online. Loans, payouts and balance repayments use only these. */
export type CashPaymentType = 'Cash' | 'Online';

/** Policy and loan payments can also be paid by PMS ("PMS"); that amount goes to the member's balance. */
export type PaymentType = CashPaymentType | 'PMS';

export const PAID_BY_PMS: PaymentType = 'PMS';

export interface PaymentCreate {
  policy_id: number;
  amount: number;
  payment_type: PaymentType;
  payment_date: string;
  due_period?: number;
  due_start?: string;
  due_end?: string;
}

export interface PaymentPublic {
  user_id: number;
  policy_id?: number | null;
  policy_no?: string | null;
  amount: number;
  payment_type: PaymentType;
  payment_date: string;
  id: number;
  payment_number: string;
  user_name?: string | null;
  user_phone?: string | null;
  due_period?: number | null;
  due_start?: string | null;
  due_end?: string | null;
}

export interface PaymentPagination {
  data: PaymentPublic[];
  total: number;
  skip: number;
  limit: number;
}

/** One policy inside a payments-management row. */
export interface ManagementPolicyPublic {
  policy_id: number;
  policy_no: string;
  card_no?: string | null;
  start_month: string;
  status: PolicyStatus;
  paid_months: number;
  total_paid: number;
  amount_due: number;
  this_month: PolicyThisMonth;
  last_payment?: LastPaymentPublic | null;
  loan: PolicyLoanSummaryPublic;
}

/** One member row of the payments table, with all of the member's policies. */
export interface PaymentManagementPublic {
  user_id: number;
  member_id?: number | null;
  user_name?: string | null;
  user_phone?: string | null;
  user_profile?: string | null;
  aadhaar_no?: string | null;
  life_status?: string | null;
  balance?: MemberBalanceSummaryPublic | null;
  policies: ManagementPolicyPublic[];
}

/** The member's balance shown once on the member row. */
export interface MemberBalanceSummaryPublic {
  balance_due: number;
  interest_due: number;
  /** false = never had a balance (No Balance); true with balance_due 0 = Cleared. */
  has_history: boolean;
}

export interface PaymentManagementSummary {
  total_members: number;
  total_policies: number;
  members_with_loan: number;
  policy_paid_this_month: number;
  policy_unpaid_this_month: number;
  policy_pending_amount: number;
  loan_paid_this_month: number;
  loan_unpaid_this_month: number;
  loan_due_amount: number;
  policy_collected: number;
  loan_repaid: number;
  loan_outstanding: number;
  loans_active: number;
  loans_given: number;
  member_balance_due?: number;
  members_with_balance?: number;
  balance_interest_charged?: number;
}

export interface PaymentManagementResponse {
  data: PaymentManagementPublic[];
  summary: PaymentManagementSummary;
  total: number;
  skip: number;
  limit: number;
}

/** Sent by every paged list: limit is the table's rows per page; search covers all pages. */
export interface PageQuery {
  skip: number;
  limit: number;
  search?: string;
}
